import { useNavigate } from 'react-router-dom';

import { RevealBtn } from '../../features/revel-btn/revel-btn';
import { roomFooterTextHandler } from '../../utils/room-footer-text-handler';

import { GameStatusEnum, MintingEnum } from 'modules/Room/game-status/constants/game-status-enum';
import { MobileHistoryMoves } from 'modules/Room/mobile/MobileHistoryMoves/mobile-history-moves';

import PrimaryButton from 'shared/components/ui/PrimaryButton/PrimaryButton';
import SecondaryBtn from 'shared/components/ui/SecondaryBtn/SecondaryBtn';
import { RoomFooterText } from 'shared/components/ui/room-footer-text/room-footer-text';
import { ROUTES } from 'shared/constants';
import { MovesEnum, RoomStatusEnum } from 'shared/constants/shared-enums';
import { useAccount } from 'shared/hooks/use-web3';

import ControllerIcon from 'assets/controller-brown.svg?react';

interface MobileRoomFooterProps {
  gameStatus: GameStatusEnum;
  isGameFinished: boolean;
  activeRoomId: string | undefined;
  address: string;
  chainId: number | undefined;
  roundCounter: number;
  expire: boolean;
  roomExpire: boolean;
  showCloseButton: boolean;
  closeUnusedRoomHandler: () => void;
  playMoves: Record<string, { move: MovesEnum }> | undefined;
  amIWinner: boolean;
  handleRevengeRequest: () => void;
  isStarting: boolean;
  isApproving: boolean;
  roomStatus: RoomStatusEnum;
  mintingLoader: MintingEnum;
  isSuccess: boolean;
  handleExit: (roomId: bigint) => void;
}

export const MobileRoomFooter = ({
  gameStatus,
  isGameFinished,
  activeRoomId,
  address,
  chainId,
  roundCounter,
  expire,
  roomExpire,
  showCloseButton,
  closeUnusedRoomHandler,
  playMoves,
  amIWinner,
  handleRevengeRequest,
  isStarting,
  isApproving,
  roomStatus,
  mintingLoader,
  isSuccess,
  handleExit,
}: MobileRoomFooterProps) => {
  const navigate = useNavigate();
  const { chain } = useAccount();

  if (gameStatus === GameStatusEnum.NO_ONE_JOIN) {
    return (
      <div className="flex w-full flex-col justify-between gap-1 sm:gap-4">
        <SecondaryBtn
          className="w-full"
          text={'modals.takeABetBack.collectButton'}
          onClick={() => handleExit(BigInt(activeRoomId || 0))}
        />
        <RoomFooterText children={<>{roomFooterTextHandler({ chain, activeRoomId })}</>} />
      </div>
    );
  }

  if (isGameFinished) {
    return (
      <div className="flex w-full flex-col justify-between gap-1 sm:gap-4">
        <div className="flex items-center gap-2">
          {!amIWinner && (
            <SecondaryBtn
              onClick={handleRevengeRequest}
              className="w-1/2"
              text="finishView.revenge"
              isLoading={isStarting || isApproving}
            />
          )}

          <PrimaryButton
            className={` ${amIWinner ? 'w-full' : 'w-1/2'}`}
            onClick={() => {
              navigate(ROUTES.LOBBY);
            }}
            svg={<ControllerIcon className={`${amIWinner ? 'h-5 w-5 sm:h-6 sm:w-6' : 'hidden'}`} />}
            text="finishView.playAgain"
          />
        </div>

        <RoomFooterText children={<>{roomFooterTextHandler({ chain, activeRoomId })}</>} />
      </div>
    );
  }

  if (
    gameStatus === GameStatusEnum.WAIT_YOUR_REVEAL &&
    !showCloseButton &&
    !expire &&
    !roomExpire &&
    roomStatus === RoomStatusEnum.ReadyForReveal &&
    mintingLoader !== MintingEnum.YOUR_REVEAL
  ) {
    return (
      <div className="flex w-full flex-col items-center justify-between gap-1">
        <RevealBtn className="w-full" />
        <RoomFooterText children={<>{roomFooterTextHandler({ chain, activeRoomId })}</>} />
      </div>
    );
  }

  if ((roomExpire || expire) && !isSuccess) {
    return (
      <div className="flex w-full flex-col items-center justify-between gap-1">
        <PrimaryButton
          text={'common.technicalDefeat'}
          onClick={closeUnusedRoomHandler}
          className="w-full"
        />
        <RoomFooterText children={<>{roomFooterTextHandler({ chain, activeRoomId })}</>} />
      </div>
    );
  }

  return (
    <MobileHistoryMoves
      move={playMoves?.[activeRoomId! + address + chainId + roundCounter]?.move}
    />
  );
};
